// @flow
import "whatwg-fetch";
import paramify from "./paramify";
import getError from "./getError";
import Promise from "bluebird";

// Helpers
// fetch API does not reject on HTTP error statuses by default, we have to
// handle this ourselves (https://github.com/github/fetch#handling-http-error-statuses)
function checkStatus(
  response,
  defaultFormat: string,
  requestedHeaders: Array<string> = []
) {
  const format = response.headers.get("Content-Type") || defaultFormat;

  if (response.status >= 200 && response.status < 400) {
    switch (format.toLowerCase()) {
      case "blob":
      case "image/png":
        return response.blob();
      case "json":
      case "application/json":
      case "application/json;charset=utf-8":
      case "application/json; charset=utf-8":
        if (requestedHeaders.length) {
          let headers = {};
          requestedHeaders.forEach(
            (h) => (headers[h] = response.headers.get(h))
          );
          return response.json().then((json) => ({ json, headers }));
        }
        return response.text().then((bodyText) => {
          try {
            return JSON.parse(bodyText);
          } catch (e) {
            return bodyText;
          }
        });
      default:
        return response.text();
    }
  } else if (
    response.status === 500 &&
    !format.toLowerCase().includes("application/json")
  ) {
    return new Promise((resolve, reject) => {
      reject({ status: response.status, message: "An error has occurred." });
    });
  } else if (response.status === 401) {
    return new Promise((resolve, reject) => {
      reject({ status: response.status, message: "Login Expired." });
    });
  } else if (response.status === 403) {
    return new Promise((resolve, reject) => {
      reject({ status: response.status, message: "Access Denied." });
    });
  } else if (response.status === 406) {
    return new Promise((resolve, reject) => {
      reject({ status: response.status, message: "Frame render failure." });
    });
  } else if (response.status === 404) {
    return new Promise((resolve, reject) => {
      reject({ status: response.status, message: "Not Found" });
    });
  } else {
    return new Promise((resolve, reject) => {
      try {
        return response.json().then((json) => {
          if (json.error && json.error[0] === 1010) {
            reject({ status: 1010, message: "Stale Data Exception" });
          } else {
            reject({ ...json, status: response.status });
          }
        });
      } catch (e) {
        return reject({
          status: response.status,
          message: response.statusText,
        });
      }
    });
  }
}

function getHeaders(accessToken, customHeaders) {
  const headers = new Headers();

  if (accessToken) {
    headers.set("Authorization", `Bearer ${accessToken}`);
  }

  headers.set("Accept", "application/json");

  Object.keys(customHeaders).map((key) => {
    return headers.set(key, customHeaders[key]);
  });
  return headers;
}

function logRequest(
  logger: ?Object,
  url: string,
  loggingThreshold: number,
  startingTimestamp: number
) {
  const requestTime = Date.now() - startingTimestamp;

  if (logger && requestTime >= loggingThreshold) {
    logger.log(` Request to URL: ${url}  took ${requestTime}ms`);
    logger.send();
  }
}

function logError(logger: ?Object, url: string, err) {
  if (logger) {
    let errorInfo = [];

    if (err.status) errorInfo.push(`[${err.status}]:`);
    if (err.message) errorInfo.push(`message - ${err.message}`);

    logger.log(`FAILED Request to URL: ${url}  ${errorInfo.join(" ")}`);
    logger.send();
  }
}

type Params = {
  accessToken: ?string,
  format: ?string,
  headers: Object,
  params: Object,
  url: string,
  logger: ?Object,
  loggingThreshold: ?number,
};

export function get(params: Params, responseHeaders: Array<string> = []) {
  const {
    accessToken,
    format = "json",
    headers = {},
    params: requestParams,
    url,
    logger,
    loggingThreshold,
  } = params;
  const urlWithParams = `${url}${
    requestParams ? `?${paramify(requestParams)}` : ""
  }`;
  const startingTimestamp = Date.now();

  return new Promise((resolve, reject) => {
    fetch(urlWithParams, {
      method: "GET",
      headers: getHeaders(accessToken, headers),
      mode: "cors",
      ...withCredentials(url),
    })
      .then((res: Response) => checkStatus(res, format, responseHeaders))
      .then((res) => {
        logRequest(logger, urlWithParams, loggingThreshold, startingTimestamp);
        return resolve(res);
      })
      .catch((err: Object) => {
        logError(logger, `GET: ${urlWithParams}`, err);
        return reject(getError(err, `GET ${JSON.stringify(params)}`));
      });
  });
}

/**
 * This helper method does a POST with the fetch api
 * @param accessToken
 * @param params
 * @param headers
 * @param url
 * returns {Promise}
 */
export function post({
  accessToken,
  headers = {},
  params,
  url,
  formData,
  logger,
}) {
  return new Promise((resolve, reject) => {
    fetch(url, {
      method: "POST",
      body: formData || JSON.stringify(params),
      headers: getHeaders(accessToken, headers),
      mode: "cors",
      ...withCredentials(url),
    })
      .then((response) => checkStatus(response, "json"))
      .then(resolve)
      .catch((err) => {
        logError(logger, `POST: ${url}`, err);
        reject(getError(err));
      });
  });
}

/**
 * This helper method does a PUT with the fetch api
 * @param accessToken
 * @param params
 * @param headers
 * @param url
 * returns {Promise}
 */
export function put({ accessToken, headers = {}, params, query, url, logger }) {
  const urlWithParams = `${url}${query ? `?${paramify(query)}` : ""}`;
  return new Promise((resolve, reject) => {
    fetch(urlWithParams, {
      method: "PUT",
      body: JSON.stringify(params),
      headers: getHeaders(accessToken, headers),
      ...withCredentials(url),
    })
      .then(checkStatus)
      .then(resolve)
      .catch((err) => {
        logError(logger, `PUT: ${url}`, err);
        reject(getError(err));
      });
  });
}

/**
 * This helper method does a PUT with the fetch api
 * @param accessToken
 * @param params
 * @param headers
 * @param url
 * returns {Promise}
 */
export function patch({
  accessToken,
  headers = {},
  params,
  query,
  url,
  logger,
}) {
  const urlWithParams = `${url}${query ? `?${paramify(query)}` : ""}`;
  return new Promise((resolve, reject) => {
    fetch(urlWithParams, {
      method: "PATCH",
      body: JSON.stringify(params),
      headers: getHeaders(accessToken, headers),
      ...withCredentials(url),
    })
      .then(checkStatus)
      .then(resolve)
      .catch((err) => {
        logError(logger, `PATCH: ${url}`, err);
        reject(getError(err));
      });
  });
}

/**
 * This helper method does a DELETE with the fetch api, but is named 'destroy' because delete is a reserved word
 * @param accessToken
 * @param params
 * @param headers
 * @param url
 * returns {Promise}
 */
export function destroy({
  accessToken,
  headers = {},
  params: requestParams,
  url,
  logger,
}) {
  const urlWithParams = `${url}${
    requestParams ? `?${paramify(requestParams)}` : ""
  }`;
  return new Promise((resolve, reject) => {
    fetch(urlWithParams, {
      method: "DELETE",
      headers: getHeaders(accessToken, headers),
      mode: "cors",
      ...withCredentials(url),
    })
      .then((response) => checkStatus(response, "text"))
      .then(resolve)
      .catch((err) => {
        logError(logger, `DELETE: ${url}`, err);
        reject(getError(err));
      });
  });
}

/**
 * We can remove this logic once all the external services are set up to specify the allowed origins
 * rather than returning a wildcard.  Until then we will have to use this white listing approach.
 *
 */

const CREDENTIALS_INCLUDED_URLS = [
  "https://fastreport.fastmodelsports.com",
  "https://fastreport-dev.fastmodelsports.com",
  "https://fastreport-demo.fastmodelsports.com",
  "https://fastreport-staging.fastmodelsports.com",
  "https://id-dev.fastmodelsports.com",
  "https://id-staging.fastmodelsports.com",
  "https://id.fastmodelsports.com",
];

function withCredentials(url): Object {
  return CREDENTIALS_INCLUDED_URLS.some((fastReportUrl) =>
    url.includes(fastReportUrl)
  )
    ? { credentials: "include" }
    : {};
}
